import {createSlice, nanoid, PayloadAction} from '@reduxjs/toolkit';
import api from '../api';
import { AerialViewState, SceneCameraState } from './camera-slice';
import { SettingsState } from './settings-slice';

export interface Bookmark {
  id: string;
  name: string;
  date: string;
  position: number[];
  visible: boolean;
  visibleAssets: string[],
  settings: SettingsState;
  aerialState: AerialViewState;
  sceneState: SceneCameraState;
}

const getByID = (state, bookmarkID: string) => {
  return state.find(bookmark => bookmark.id === bookmarkID);
};

export const bookmarksSlice = createSlice({
  name: 'bookmarks',
  initialState: [] as Bookmark[],
  reducers: {
    createBookmark: (state, action: PayloadAction<{
      name: string,
      position: number[],
      visibleAssets: string[],
      settings: SettingsState,
      aerialState: AerialViewState,
      sceneState: SceneCameraState
    }>) => {
      const attributes = action.payload;

      const bookmark = {
        id: nanoid(),
        date: new Date().toISOString(),
        visible: true,
        ...attributes
      };

      state.push(bookmark);

      // Update cloud site
      api.bookmarks.create(bookmark);
    },
    updateBookmark: (state, action: PayloadAction<{
      bookmarkID: string,
      name?: string,
      position?: number[],
      visible?: boolean,
      visibleAssets?: string[],
      settings?: SettingsState,
      aerialState?: AerialViewState,
      sceneState?: SceneCameraState;
    }>) => {
      const {bookmarkID, ...updates} = action.payload;

      const bookmark = getByID(state, bookmarkID);
      if (!bookmark) return;

      for (let key in updates) {
        if (bookmark.hasOwnProperty(key)) {
          bookmark[key] = updates[key];
        };
      };

      // Update modified date
      bookmark.date = new Date().toISOString();

      // Update cloud site
      api.bookmarks.update(bookmarkID, {...updates, date: bookmark.date});
    },
    deleteBookmark: (state, action: PayloadAction<string>) => {
      const bookmarkID = action.payload;
      const modified = state.filter(bookmark => bookmark.id !== bookmarkID);

      // Update cloud site
      api.bookmarks.delete(bookmarkID);

      return modified;
    },

    /** Websocket specific */
    wsCreateBookmark: (state, action: PayloadAction<Bookmark>) => {
      const bookmark = action.payload;
      if (getByID(state, bookmark.id)) return;

      state.push(bookmark);
    },
    wsUpdateBookmark: (state, action: PayloadAction<Bookmark>) => {
      const {id, ...updates} = action.payload;

      let bookmark = getByID(state, id);
      if (!bookmark) return;

      for (let key in updates) {
        if (bookmark.hasOwnProperty(key)) {
          bookmark[key] = updates[key];
        };
      };
    },
    wsDeleteBookmark: (state, action: PayloadAction<string>) => {
      const bookmarkID = action.payload;
      return state.filter(bookmark => bookmark.id !== bookmarkID);
    },
  }
});

export default bookmarksSlice.reducer;

export const {
  wsCreateBookmark,
  wsUpdateBookmark,
  wsDeleteBookmark,
  createBookmark,
  deleteBookmark,
  updateBookmark,
} = bookmarksSlice.actions;

export const selectAllBookmarks = state => state.bookmarks as Bookmark[];
